import { VideoInfo, EFFECT_DEFINITIONS } from './types';
import {configs} from '../../resources/configs';
import {getAccessToken} from '../../utils/AuthUtill';

const API_BASE_URL = configs.apiUrl;

function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken() || '';
  return {
    'authorization': token,
    'content-type': 'application/json',
  };
}

export async function getUploadUrl(filename: string, contentType: string = 'video/mp4'): Promise<{ uploadUrl: string; videoId: string; s3Key: string; s3Url: string }> {
  const response = await fetch(`${API_BASE_URL}/template-auto/video/upload-url`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ filename, contentType }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get upload URL');
  }

  return response.json();
}

export async function uploadToS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'video/mp4',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to upload file to S3');
  }
}

const PROGRESS_LABELS: Record<string, string> = {
  download: 'Загрузка видео на сервер...',
  process: 'Обнаружение сцен...',
  filmstrip: 'Генерация filmstrip...',
  upload: 'Сохранение результатов...',
};

export function processVideoWs(
  s3Url: string,
  filename: string,
  threshold: number = 27.0,
  onProgress: (message: string) => void,
): Promise<VideoInfo> {
  return new Promise((resolve, reject) => {
    const wsUrl = configs.templateAutoWsUrl as string;
    if (!wsUrl) {
      reject(new Error('templateAutoWsUrl не настроен в configs'));
      return;
    }

    const ws = new WebSocket(wsUrl);
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      ws.close();
      fn();
    };

    // 15-minute safety timeout matching Lambda max
    const timeout = setTimeout(() => {
      finish(() => reject(new Error('Превышено время ожидания обработки видео')));
    }, 15 * 60 * 1000);

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: 'process', s3Url, filename, threshold }));
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data as string);
      switch (msg.type) {
        case 'progress':
          onProgress(PROGRESS_LABELS[msg.step] ?? msg.message);
          break;
        case 'complete':
          clearTimeout(timeout);
          finish(() => resolve(msg.data as VideoInfo));
          break;
        case 'error':
          clearTimeout(timeout);
          finish(() => reject(new Error(msg.message || 'Ошибка обработки видео')));
          break;
      }
    };

    ws.onerror = () => {
      clearTimeout(timeout);
      finish(() => reject(new Error('Ошибка WebSocket соединения')));
    };

    ws.onclose = (event) => {
      clearTimeout(timeout);
      if (!settled && !event.wasClean) {
        finish(() => reject(new Error('WebSocket соединение прервано')));
      }
    };
  });
}

export async function getFilmstripUrl(videoId: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/template-auto/video/${videoId}/filmstrip`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get filmstrip URL');
  }

  const data = await response.json();
  return data.filmstripUrl;
}

export async function getVideoStreamUrl(videoId: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/template-auto/video/${videoId}/stream-url`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to get video stream URL');
  }

  const data = await response.json();
  return data.streamUrl;
}

export async function exportTimings(
  videoId: string,
  markers: number[],
  duration: number,
  effectMarkers: any[]
): Promise<string> {
  // Get category for each effect from definitions using effectId
  const effectMarkersWithCategory = effectMarkers.map(em => {
    const def = EFFECT_DEFINITIONS.find(d => d.id === em.effectId);
    return {
      effect_code: em.effectCode,  // Use full code like B1, O2, etc.
      category: def?.category || em.category || 'clip',
      time: em.time,
      params: em.params,
    };
  });

  const response = await fetch(`${API_BASE_URL}/template-auto/video/${videoId}/export`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      markers,
      video_duration: duration,
      effect_markers: effectMarkersWithCategory,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to export timings');
  }

  return response.text();
}
